import SettingsCellIcon from '@mui/icons-material/SettingsCell';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import {
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Grid,
  Divider,
  Button,
} from '@mui/material';
import { IconButton } from '@mui/material';
import { Tooltip } from '@mui/material';
import { Dialog } from '@mui/material';
import { useState } from 'react';
import ReactJson from 'react-json-view';

type HardwareInfo = {
  brand: string;
  model: string;
  osVersion: string;
  deviceName: string;
  deviceType: number;
  systemVersion: number;
  osBuildId: string;
  manufacturer: string;
  modelId: string;
  modelName: string;
  totalMemory: number;
  freeDiskSpaceBytes: number;
  supportedCpuArchitectures: string[];
  timezone: {
    name: string;
    offset: number;
    gmt: string;
  };
};

export type HardwareInfoButtonProps = {
  info: HardwareInfo | null | undefined;
};

export const HardwareInfoButton = ({
  info,
}: HardwareInfoButtonProps) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [viewMode, setViewMode] = useState<'friendly' | 'json'>(
    'friendly',
  );

  const handleCopyToClipboard = async () => {
    await navigator.clipboard.writeText(
      JSON.stringify(info, null, 2),
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatMemory = (bytes: number) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    }
    if (bytes < 1024 * 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  return (
    <>
      <Tooltip title="View hardware info">
        <IconButton onClick={() => setOpen(true)}>
          <SettingsCellIcon />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6">Hardware Info</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={
                viewMode === 'friendly' ? 'contained' : 'outlined'
              }
              size="small"
              onClick={() => setViewMode('friendly')}
            >
              Friendly View
            </Button>
            <Button
              variant={viewMode === 'json' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setViewMode('json')}
            >
              JSON View
            </Button>
            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
              <IconButton onClick={handleCopyToClipboard}>
                {copied ? (
                  <CheckIcon color="success" />
                ) : (
                  <ContentCopyIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewMode === 'json' ? (
            <Box
              sx={{
                backgroundColor: '#2d2d2d',
                p: 2,
                borderRadius: 1,
              }}
            >
              <ReactJson
                src={info ?? {}}
                theme="monokai"
                displayDataTypes={false}
                enableClipboard={true}
                collapsed={2}
                style={{
                  backgroundColor: 'transparent',
                  fontFamily: 'monospace',
                }}
              />
            </Box>
          ) : (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Device
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Manufacturer
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">
                      {info?.manufacturer}
                    </Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Brand
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">
                      {info?.brand}
                    </Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Model
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">
                      {info?.model}
                    </Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Model Name
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">
                      {info?.modelName}
                    </Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Device Name
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">
                      {info?.deviceName}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">
                  System
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      OS Version
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">
                      {info?.osVersion}
                    </Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      System Version
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">
                      {info?.systemVersion}
                    </Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      OS Build ID
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">
                      {info?.osBuildId}
                    </Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      CPU Architectures
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">
                      {info?.supportedCpuArchitectures.join(', ')}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Storage & Memory
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Total Memory
                    </Typography>
                  </Grid>
                  {!!info?.totalMemory && (
                    <Grid item xs={8}>
                      <Typography variant="body2">
                        {formatMemory(info?.totalMemory)}
                      </Typography>
                    </Grid>
                  )}

                  <Grid item xs={4}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Free Disk Space
                    </Typography>
                  </Grid>
                  {!!info?.freeDiskSpaceBytes && (
                    <Grid item xs={8}>
                      <Typography variant="body2">
                        {formatMemory(info?.freeDiskSpaceBytes)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Time & Location
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Timezone
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">
                      {info?.timezone?.name} ({info?.timezone?.gmt})
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
